import React, { useEffect, useState } from 'react'
import { useParams, useLocation, Link } from 'react-router-dom'
import { useSEO } from '../lib/useSEO'

export function getLocalizedValue(field) {
  if (field && typeof field === 'object') {
    if ('da' in field) return field.da
    if ('en' in field) return field.en
  }
  return field || ''
}

// Helper to parse HTML into intro and accordion items
function parseAccordionContent(html) {
  if (!html) return { intro: '', items: [] };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const introNodes = [];
    const items = [];
    
    let currentItem = null;
    
    // Iterate through body children
    const children = Array.from(doc.body.children);
    
    children.forEach((child) => {
      const tagName = child.tagName.toLowerCase();
      if (tagName === 'h3') {
        // If we hit an h3, start a new accordion item
        if (currentItem) {
          items.push(currentItem);
        }
        currentItem = {
          title: child.innerHTML,
          id: child.getAttribute('id') || '',
          contentNodes: []
        };
      } else {
        if (currentItem) {
          currentItem.contentNodes.push(child.outerHTML);
        } else {
          introNodes.push(child.outerHTML);
        }
      }
    });
    
    if (currentItem) {
      items.push(currentItem);
    }
    
    return {
      intro: introNodes.join(''),
      items: items
        .filter(item => !item.title.toLowerCase().includes('du kan finde følgende information på denne side'))
        .map(item => ({
          title: item.title,
          id: item.id,
          contentHtml: item.contentNodes.join('')
        }))
    };
  } catch (e) {
    console.error('Failed to parse accordion content', e);
    return { intro: html, items: [] };
  }
}

export default function PageDetail({ slug: propSlug }) {
  const { slug: paramSlug } = useParams()
  const slug = propSlug || paramSlug || 'index'
  const [page, setPage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openAccordionIndices, setOpenAccordionIndices] = useState({})
  const location = useLocation()

  useEffect(() => {
    setLoading(true)
    setError(null)
    
    fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Siden kunne ikke findes')
        }
        return res.json()
      })
      .then((data) => {
        setPage(data)
        setLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setError(err.message)
        setLoading(false)
      })
  }, [slug])

  // Update document metadata with SEO tags
  useSEO({
    title: page ? getLocalizedValue(page.title) : 'Vikar4 · Vikarservice & Rekruttering',
    description: page ? (getLocalizedValue(page.meta_description) || getLocalizedValue(page.og_description)) : '',
    ogTitle: page ? getLocalizedValue(page.og_title) : '',
    ogDescription: page ? getLocalizedValue(page.og_description) : '',
    canonicalPath: slug === 'index' ? '/' : `/${slug}`
  })

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
      </div>
    )
  }

  if (error || !page) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-slate-900">Siden blev ikke fundet</h1>
        <p className="mt-4 text-slate-600">Vi kunne desværre ikke finde den side, du leder efter.</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-blue-100 hover:bg-blue-700 transition-all"
        >
          Gå til forsiden
        </Link>
      </div>
    )
  }

  const contentHtml = getLocalizedValue(page.content)

  // Accordion state for privatlivspolitik
  const isPrivatlivspolitik = slug === 'privatlivspolitik'
  const { intro, items: accordionItems } = isPrivatlivspolitik 
    ? parseAccordionContent(contentHtml) 
    : { intro: '', items: [] }

  const toggleAccordion = (index) => {
    setOpenAccordionIndices(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const handleIntroClick = (e) => {
    const link = e.target.closest('a');
    if (link) {
      const href = link.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const targetId = href.substring(1);
        // Find the index of the accordion item with this ID
        const targetIndex = accordionItems.findIndex(item => item.id === targetId);
        if (targetIndex !== -1) {
          // Open the accordion
          setOpenAccordionIndices(prev => ({
            ...prev,
            [targetIndex]: true
          }));
          // Scroll to the accordion item
          setTimeout(() => {
            const element = document.getElementById(targetId || `accordion-item-${targetIndex}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      }
    }
  };

  // If it's the homepage, let's render a beautiful hero section first!
  if (slug === 'index') {
    return (
      <div>
        {/* Video Hero Section */}
        <section className="relative w-full h-screen overflow-hidden bg-slate-900">
          {/* Background Video */}
          <video
            autoPlay
            loop
            muted
            playsInline
            title="Vikar4 præsentationsvideo og kundeudtalelse fra Mammen Mejerierne"
            aria-label="Vikar4 præsentationsvideo og kundeudtalelse fra Mammen Mejerierne"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source
              src="https://assets.auxodev.dk/assets/7b6c8b8c-dd85-4faa-a1de-b9b48f1b2725/vikar4-kundeudtalelse-mammen-mejerierne.mp4"
              type="video/mp4"
            />
            Din browser understøtter ikke videoafspilning.
          </video>

          {/* White Overlay */}
          <div className="absolute inset-0 bg-white/45 md:bg-white/40 backdrop-blur-[0.5px]" />

          {/* Hero Content */}
          <div className="relative z-10 mx-auto max-w-7xl h-full px-6 md:px-12 flex flex-col justify-center pt-24 sm:pt-28">
            <div className="max-w-3xl ml-auto text-right flex flex-col items-end">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/90 backdrop-blur-sm rounded-full border border-blue-100 shadow-sm mb-4">
                <span className="w-2 h-2 rounded-full bg-[#015D9A] animate-pulse"></span>
                <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#015D9A]">
                  Vikar4 Vikarservice
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#0f3d5c] tracking-tight leading-[1.15] mb-6">
                <span className="whitespace-nowrap">Professionelt <span className="text-[#015D9A]">vikarbureau</span></span>
                <span className="block mt-1 sm:mt-2 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800">
                  med fleksible løsninger
                </span>
              </h1>
              <div className="flex flex-wrap gap-3.5 justify-end w-full">
                <Link
                  to="/kontakt"
                  className="rounded-md bg-[#015D9A] px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#014d80] shadow-md flex items-center gap-2"
                >
                  <span>Kontakt os nu</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/bliv-vikar"
                  className="rounded-md border border-slate-300 bg-white/90 backdrop-blur-sm px-6 py-3.5 text-sm font-semibold text-slate-700 transition-all hover:bg-white hover:border-slate-400 shadow-sm"
                >
                  Bliv vikar
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              
              {/* Left Column: Primary SEO & Content Sections (8 of 12 columns) */}
              <div className="lg:col-span-8 space-y-12">
                
                {/* 1. Main Headline, Value Badges & Intro */}
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-[#015D9A] bg-blue-50/90 border border-blue-100/80 px-3.5 py-1.5 rounded-full shadow-xs">
                    <i className="fa-solid fa-briefcase text-xs" aria-hidden="true" />
                    <span>Vikarservice &amp; Rekruttering</span>
                  </div>

                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0f3d5c] leading-[1.2]">
                    Vikarbureau i Nordjylland, Midtjylland og hele Danmark med fleksible løsninger
                  </h1>

                  <p className="text-lg text-slate-700 leading-relaxed font-normal">
                    Når din virksomhed mangler ekstra hænder, eller når du leder efter et fleksibelt job, hjælper <strong className="text-[#0f3d5c] font-semibold">Vikar4</strong> med at skabe den rette forbindelse. Vi er et engageret vikarbureau med stærke rødder i Nordjylland og Midtjylland samt landsdækkende opgaveløsning i hele Danmark, hvor personlig kontakt, lokalkendskab og praktiske løsninger er en naturlig del af samarbejdet.
                  </p>

                  {/* 3 Quick Value Highlights */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 pt-2">
                    <div className="bg-slate-50 border border-slate-200/90 rounded-md p-4 flex items-start gap-3 transition-colors hover:border-blue-200 hover:bg-blue-50/30">
                      <div className="w-9 h-9 rounded-lg bg-blue-100/70 text-[#015D9A] flex items-center justify-center shrink-0">
                        <i className="fa-solid fa-location-dot text-sm" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Områder</div>
                        <div className="text-sm font-bold text-[#0f3d5c]">Nord- &amp; Midtjylland</div>
                        <div className="text-xs text-slate-600">Plus hele Danmark</div>
                      </div>
                    </div>

                    <div className="bg-slate-50 border border-slate-200/90 rounded-md p-4 flex items-start gap-3 transition-colors hover:border-blue-200 hover:bg-blue-50/30">
                      <div className="w-9 h-9 rounded-lg bg-blue-100/70 text-[#015D9A] flex items-center justify-center shrink-0">
                        <i className="fa-solid fa-bolt text-sm" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Kapacitet</div>
                        <div className="text-sm font-bold text-[#0f3d5c]">Hurtig Bemandning</div>
                        <div className="text-xs text-slate-600">Kort- &amp; langvarigt</div>
                      </div>
                    </div>

                    <div className="bg-slate-50 border border-slate-200/90 rounded-md p-4 flex items-start gap-3 transition-colors hover:border-blue-200 hover:bg-blue-50/30">
                      <div className="w-9 h-9 rounded-lg bg-blue-100/70 text-[#015D9A] flex items-center justify-center shrink-0">
                        <i className="fa-solid fa-file-signature text-sm" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Administration</div>
                        <div className="text-sm font-bold text-[#0f3d5c]">0 besvær for jer</div>
                        <div className="text-xs text-slate-600">Vi klarer alt det formelle</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Fleksible vikarløsninger, når behovet ændrer sig */}
                <div className="space-y-6 border-t border-slate-200/80 pt-10">
                  <div>
                    <span className="text-xs uppercase font-bold tracking-wider text-[#015D9A]">Skalerbar bemanding</span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0f3d5c] tracking-tight mt-1 flex items-center gap-3">
                      <span className="w-1.5 h-6 bg-[#015D9A] inline-block rounded-full" />
                      Fleksible vikarløsninger, når behovet ændrer sig
                    </h2>
                  </div>

                  <p className="text-slate-700 leading-relaxed">
                    Travle perioder, ferie og svingende produktion kan hurtigt ændre behovet for medarbejdere. Med vores vikarservice får din virksomhed mulighed for at tilpasse bemandingen, uden at et midlertidigt behov nødvendigvis skal føre til en fast ansættelse eller mere administration i hverdagen.
                  </p>
                  
                  {/* Scenario Pill Highlights */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 px-3 py-1.5 rounded-md text-xs font-medium border border-slate-200">
                      <i className="fa-solid fa-umbrella-beach text-[#015D9A] text-xs" /> Ferieafløsning
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 px-3 py-1.5 rounded-md text-xs font-medium border border-slate-200">
                      <i className="fa-solid fa-chart-line text-[#015D9A] text-xs" /> Spidsbelastning &amp; sæson
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 px-3 py-1.5 rounded-md text-xs font-medium border border-slate-200">
                      <i className="fa-solid fa-user-clock text-[#015D9A] text-xs" /> Sygefravær &amp; akut mangel
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 px-3 py-1.5 rounded-md text-xs font-medium border border-slate-200">
                      <i className="fa-solid fa-cubes text-[#015D9A] text-xs" /> Produktionsudvidelse
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div className="bg-gradient-to-b from-white to-slate-50 border border-slate-200 p-5 rounded-md shadow-xs hover:border-blue-300 transition-colors">
                      <div className="flex items-center gap-2.5 text-[#015D9A] font-bold mb-2">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <i className="fa-solid fa-arrows-up-down text-xs" />
                        </div>
                        <span className="text-base text-[#0f3d5c]">Skaler op og ned efter behov</span>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Hurtig tilpasning ved sæsonudsving, spidsbelastninger eller pludselig travlhed i produktion og logistik uden lange bindingsperioder.
                      </p>
                    </div>

                    <div className="bg-gradient-to-b from-white to-slate-50 border border-slate-200 p-5 rounded-md shadow-xs hover:border-blue-300 transition-colors">
                      <div className="flex items-center gap-2.5 text-[#015D9A] font-bold mb-2">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <i className="fa-solid fa-shield-halved text-xs" />
                        </div>
                        <span className="text-base text-[#0f3d5c]">Mindre administration</span>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        I betaler kun for de effektive arbejdstimer – vi håndterer alt det formelle, løn, jura og overenskomster.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 3. Hvordan finder vi en vikar til jeres opgaver? (Visual 3-Step Process Flow) */}
                <div className="space-y-6 border-t border-slate-200/80 pt-10">
                  <div>
                    <span className="text-xs uppercase font-bold tracking-wider text-[#015D9A]">Gennemskuelig proces</span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0f3d5c] tracking-tight mt-1 flex items-center gap-3">
                      <span className="w-1.5 h-6 bg-[#015D9A] inline-block rounded-full" />
                      Hvordan finder vi en vikar til jeres opgaver?
                    </h2>
                  </div>

                  <p className="text-slate-700 leading-relaxed">
                    Vi formidler vikarer til både kortvarige opgaver og længere forløb. Før vi finder en løsning, taler vi med jer om arbejdsopgaverne, den ønskede erfaring og forholdene på arbejdspladsen. Det giver os et bedre grundlag for at vælge en vikar, som kan indgå i netop jeres drift.
                  </p>

                  {/* 3-Step Process Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-5 relative overflow-hidden">
                      <span className="absolute top-3 right-3 text-2xl font-black text-slate-200 select-none">01</span>
                      <div className="w-9 h-9 rounded-md bg-[#015D9A] text-white flex items-center justify-center mb-3.5 shadow-xs font-bold text-sm">
                        <i className="fa-solid fa-comments text-xs" />
                      </div>
                      <h3 className="font-bold text-[#0f3d5c] text-base mb-1.5">1. Behov &amp; Dialog</h3>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Vi gennemgår jeres opgaver, nødvendige kvalifikationer og arbejdsmiljø, så vi forstår jeres konkrete hverdag.
                      </p>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-md p-5 relative overflow-hidden">
                      <span className="absolute top-3 right-3 text-2xl font-black text-slate-200 select-none">02</span>
                      <div className="w-9 h-9 rounded-md bg-[#015D9A] text-white flex items-center justify-center mb-3.5 shadow-xs font-bold text-sm">
                        <i className="fa-solid fa-user-check text-xs" />
                      </div>
                      <h3 className="font-bold text-[#0f3d5c] text-base mb-1.5">2. Match &amp; Udvælgelse</h3>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Vi matcher jer med motiverede vikarer med de rette kompetencer, så opgaveløsningen fungerer fra første minut.
                      </p>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-md p-5 relative overflow-hidden">
                      <span className="absolute top-3 right-3 text-2xl font-black text-slate-200 select-none">03</span>
                      <div className="w-9 h-9 rounded-md bg-[#015D9A] text-white flex items-center justify-center mb-3.5 shadow-xs font-bold text-sm">
                        <i className="fa-solid fa-handshake text-xs" />
                      </div>
                      <h3 className="font-bold text-[#0f3d5c] text-base mb-1.5">3. Tryg Opstart</h3>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Vi deltager gerne på 1. arbejdsdag for en god start og håndterer alt det administrative løbende.
                      </p>
                    </div>
                  </div>
                  
                  {/* Administrative Advantage Highlight Card */}
                  <div className="bg-gradient-to-r from-blue-50/80 via-blue-50/40 to-slate-50 border border-blue-200/90 rounded-md p-6 relative overflow-hidden">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 pb-4 border-b border-blue-200/60">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#015D9A] text-white flex items-center justify-center shrink-0">
                          <i className="fa-solid fa-clipboard-check text-base" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-[#0f3d5c]">
                            Vi tager det fulde administrative ansvar
                          </h3>
                          <p className="text-xs text-[#015D9A] font-semibold">
                            Tryghed for både virksomhed og vikar
                          </p>
                        </div>
                      </div>
                    </div>

                    <p className="text-slate-700 leading-relaxed text-sm mb-4">
                      Som kunde slipper I for selv at håndtere kontrakt, løn, G-dage og feriepenge for vikaren. Vi tager os af de administrative forhold, mens I kan koncentrere jer om opgaverne. Inden opstart taler vi med vikaren, og vi deltager på den første arbejdsdag for at skabe en tryg begyndelse.
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
                      <div className="bg-white/90 border border-blue-100 rounded px-3 py-2 text-xs font-semibold text-slate-700 flex items-center gap-2">
                        <i className="fa-solid fa-check text-[#015D9A]" />
                        <span>Ansættelseskontrakt</span>
                      </div>
                      <div className="bg-white/90 border border-blue-100 rounded px-3 py-2 text-xs font-semibold text-slate-700 flex items-center gap-2">
                        <i className="fa-solid fa-check text-[#015D9A]" />
                        <span>Løn &amp; Feriepenge</span>
                      </div>
                      <div className="bg-white/90 border border-blue-100 rounded px-3 py-2 text-xs font-semibold text-slate-700 flex items-center gap-2">
                        <i className="fa-solid fa-check text-[#015D9A]" />
                        <span>G-dage &amp; Sygedagpenge</span>
                      </div>
                      <div className="bg-white/90 border border-blue-100 rounded px-3 py-2 text-xs font-semibold text-slate-700 flex items-center gap-2">
                        <i className="fa-solid fa-check text-[#015D9A]" />
                        <span>Førstedagsdeltagelse</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4. Et lokalt & regionalt vikarbureau med kendskab til jeres hverdag */}
                <div className="space-y-6 border-t border-slate-200/80 pt-10">
                  <div>
                    <span className="text-xs uppercase font-bold tracking-wider text-[#015D9A]">Nærvær og lokalkendskab</span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0f3d5c] tracking-tight mt-1 flex items-center gap-3">
                      <span className="w-1.5 h-6 bg-[#015D9A] inline-block rounded-full" />
                      Et vikarbureau med kendskab til jeres hverdag i Nordjylland, Midtjylland &amp; hele Danmark
                    </h2>
                  </div>

                  <p className="text-slate-700 leading-relaxed">
                    Søger du et vikarbureau i Nordjylland, Midtjylland eller øvrige Danmark, får du hos Vikar4 en stærk og nærværende samarbejdspartner, der lægger vægt på direkte dialog og dyb forståelse for de praktiske forhold bag et bemandingsbehov.
                  </p>
                  
                  <p className="text-slate-700 leading-relaxed">
                    Vi hjælper især virksomheder i Nordjylland og Midtjylland – herunder i byer og områder som Aalborg, Hjørring, Frederikshavn, Hobro, Randers, Viborg, Silkeborg og Aarhus – samt med skræddersyede vikarløsninger til virksomheder i hele landet. Som vikarbureau vil vi hellere kende behovet og arbejdspladsen ordentligt end at levere en standardløsning. Det gør samarbejdet mere nærværende og lettere at tilpasse.
                  </p>

                  {/* Mission & Vision Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div className="bg-blue-50/60 border border-blue-200/80 p-5 rounded-md space-y-2">
                      <div className="flex items-center gap-2.5 text-[#015D9A] font-bold">
                        <i className="fa-solid fa-bullseye text-base" />
                        <span className="uppercase tracking-wider text-xs font-extrabold">Vores mission</span>
                      </div>
                      <p className="text-slate-700 text-sm leading-relaxed">
                        Gennem nærhed, tryghed og omsorg for vores vikarer leverer vi kundetilpassede bemandingsløsninger, der styrker virksomhedernes bundlinje og drift.
                      </p>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 p-5 rounded-md space-y-2">
                      <div className="flex items-center gap-2.5 text-[#0f3d5c] font-bold">
                        <i className="fa-solid fa-compass text-base text-[#015D9A]" />
                        <span className="uppercase tracking-wider text-xs font-extrabold">Vores vision</span>
                      </div>
                      <p className="text-slate-700 text-sm leading-relaxed">
                        Vi vil være det foretrukne vikarbureau for virksomheder i Nordjylland, Midtjylland og resten af Danmark, der efterspørger engagerede, motiverede og produktive vikarer.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 5. Vikarservice til spidsbelastning, ferie og ekstra kapacitet */}
                <div className="space-y-5 border-t border-slate-200/80 pt-10">
                  <div>
                    <span className="text-xs uppercase font-bold tracking-wider text-[#015D9A]">Praktisk erfaring</span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0f3d5c] tracking-tight mt-1 flex items-center gap-3">
                      <span className="w-1.5 h-6 bg-[#015D9A] inline-block rounded-full" />
                      Vikarservice til spidsbelastning, ferie og ekstra kapacitet
                    </h2>
                  </div>

                  <p className="text-slate-700 leading-relaxed">
                    Behovet kan være planlagt eller opstå med kort varsel. Det kan handle om ferieafløsning, travlhed i produktionen, fravær eller en periode med flere opgaver end normalt. Vores fleksible vikarløsninger kan tilpasses både opgavens indhold og den periode, hvor I mangler arbejdskraft.
                  </p>
                  
                  <div className="bg-slate-50 border-l-4 border-[#015D9A] border-y border-r border-slate-200/80 p-5 rounded-r-md">
                    <p className="text-slate-700 leading-relaxed text-sm sm:text-base italic">
                      "Vi har praktisk erfaring med produktion, personale og planlægning. Derfor ved vi, at bemanding ikke kun handler om at finde et ledigt navn. Vikaren skal kende opgaven, være forberedt på arbejdsdagen og have de rette forudsætninger for at bidrage på en ansvarlig måde fra start."
                    </p>
                  </div>
                </div>

                {/* 6. Vil du have et vikarjob, der passer til din hverdag? */}
                <div className="space-y-6 border-t border-slate-200/80 pt-10">
                  <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50/40 border border-slate-200 rounded-md p-6 sm:p-7 space-y-5">
                    <div>
                      <span className="text-xs uppercase font-bold tracking-wider text-[#015D9A]">For jobsøgende &amp; vikarer</span>
                      <h2 className="text-2xl sm:text-3xl font-bold text-[#0f3d5c] tracking-tight mt-1">
                        Vil du have et vikarjob, der passer til din hverdag?
                      </h2>
                    </div>

                    <p className="text-slate-700 leading-relaxed">
                      Et job som vikar kan være relevant, hvis du er ledig, studerende, holder sabbatår eller gerne vil tjene ekstra. Du kan få erfaring fra forskellige arbejdspladser og prøve nye opgaver, mens arbejdstider og varighed kan variere. Nogle vikarjob ligger også uden for normal arbejdstid.
                    </p>

                    {/* Who can apply target pills */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="bg-white border border-slate-200 rounded p-3 text-center">
                        <i className="fa-solid fa-graduation-cap text-[#015D9A] text-lg mb-1" />
                        <div className="text-xs font-bold text-[#0f3d5c]">Studerende &amp; sabbatår</div>
                        <div className="text-[11px] text-slate-500">Fleksible vagter</div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded p-3 text-center">
                        <i className="fa-solid fa-briefcase text-[#015D9A] text-lg mb-1" />
                        <div className="text-xs font-bold text-[#0f3d5c]">Ledige &amp; faglærte</div>
                        <div className="text-[11px] text-slate-500">Ny erfaring &amp; netværk</div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded p-3 text-center">
                        <i className="fa-solid fa-wallet text-[#015D9A] text-lg mb-1" />
                        <div className="text-xs font-bold text-[#0f3d5c]">Ekstra indkomst</div>
                        <div className="text-[11px] text-slate-500">Deltid eller fuldtid</div>
                      </div>
                    </div>

                    <p className="text-slate-700 leading-relaxed text-sm">
                      Send os dit CV eller en kort beskrivelse af dine kvalifikationer og din erhvervserfaring. Vi bruger oplysningerne til at vurdere, hvilke opgaver der kan passe til dig. Før du begynder, taler vi med dig om jobbet, så du møder forberedt og ved, hvem du kan kontakte undervejs.
                    </p>
                    
                    <div className="flex flex-wrap gap-3.5 pt-1">
                      <Link
                        to="/bliv-vikar"
                        className="inline-flex items-center gap-2 rounded-md bg-[#015D9A] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#014d80] shadow-xs"
                      >
                        <span>Læs mere om at blive vikar</span>
                        <i className="fa-solid fa-arrow-right text-xs" />
                      </Link>
                      <a
                        href="mailto:job@vikar4.dk"
                        className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition-all hover:bg-slate-50 hover:border-slate-400 shadow-xs"
                      >
                        <i className="fa-solid fa-envelope text-[#015D9A]" />
                        <span>Send dit CV direkte</span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* 7. Quick Navigation Box: For Virksomheder & For Jobsøgere */}
                <div className="border-t border-slate-200/80 pt-10 space-y-6">
                  <div>
                    <span className="text-xs uppercase font-bold tracking-wider text-[#015D9A]">Genveje &amp; Information</span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#0f3d5c] tracking-tight mt-1 flex items-center gap-3">
                      <span className="w-1.5 h-6 bg-[#015D9A] inline-block rounded-full" />
                      Udforsk vores løsninger
                    </h2>
                    <p className="text-slate-600 text-sm mt-1">
                      Vælg den sektion der passer til dine behov for at læse mere om vores services og erfaringer.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Boks 1: For Virksomheder */}
                    <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-md p-5 sm:p-6 flex flex-col justify-between hover:border-blue-300 transition-all">
                      <div>
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#015D9A] bg-blue-50 px-2.5 py-1 rounded">
                            <i className="fa-solid fa-building text-xs" />
                            <span>For virksomheder</span>
                          </div>
                          <span className="text-xs text-slate-500 font-medium">Bemandingsløsninger</span>
                        </div>
                        <h3 className="text-lg font-bold text-[#0f3d5c] mb-1">
                          Mangler I ekstra hænder?
                        </h3>
                        <p className="text-xs text-slate-600 leading-relaxed mb-4">
                          Få et komplet overblik over, hvordan vi hjælper jeres virksomhed med fleksibel arbejdskraft og tryghed.
                        </p>

                        <div className="space-y-2.5">
                          <Link
                            to="/hvad-tilbyder-vi"
                            className="group block bg-white border border-slate-200 hover:border-[#015D9A] rounded p-3 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="font-bold text-sm text-[#0f3d5c] group-hover:text-[#015D9A] transition-colors flex items-center gap-2">
                                <i className="fa-solid fa-circle-chevron-right text-xs text-[#015D9A]" />
                                <span>Hvad tilbyder vi?</span>
                              </div>
                              <i className="fa-solid fa-arrow-right text-xs text-slate-400 group-hover:text-[#015D9A] group-hover:translate-x-0.5 transition-all" />
                            </div>
                            <p className="text-xs text-slate-500 mt-1 pl-5">
                              Læs om vores fleksible vikarservice, rekruttering og administrative fordele.
                            </p>
                          </Link>

                          <Link
                            to="/referencer"
                            className="group block bg-white border border-slate-200 hover:border-[#015D9A] rounded p-3 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="font-bold text-sm text-[#0f3d5c] group-hover:text-[#015D9A] transition-colors flex items-center gap-2">
                                <i className="fa-solid fa-circle-chevron-right text-xs text-[#015D9A]" />
                                <span>Referencer</span>
                              </div>
                              <i className="fa-solid fa-arrow-right text-xs text-slate-400 group-hover:text-[#015D9A] group-hover:translate-x-0.5 transition-all" />
                            </div>
                            <p className="text-xs text-slate-500 mt-1 pl-5">
                              Se hvad vores samarbejdspartnere og erhvervskunder siger om samarbejdet.
                            </p>
                          </Link>
                        </div>
                      </div>

                      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                        <Link
                          to="/kontakt"
                          className="text-xs font-bold text-[#015D9A] hover:underline inline-flex items-center gap-1.5"
                        >
                          <span>Få et uforpligtende tilbud</span>
                          <i className="fa-solid fa-arrow-right text-[10px]" />
                        </Link>
                      </div>
                    </div>

                    {/* Boks 2: For Jobsøgere */}
                    <div className="bg-gradient-to-b from-blue-50/40 via-white to-slate-50 border border-slate-200 rounded-md p-5 sm:p-6 flex flex-col justify-between hover:border-blue-300 transition-all">
                      <div>
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#015D9A] bg-blue-100/70 px-2.5 py-1 rounded">
                            <i className="fa-solid fa-user-tie text-xs" />
                            <span>For jobsøgere</span>
                          </div>
                          <span className="text-xs text-slate-500 font-medium">Jobmuligheder</span>
                        </div>
                        <h3 className="text-lg font-bold text-[#0f3d5c] mb-1">
                          Søger du et vikarjob?
                        </h3>
                        <p className="text-xs text-slate-600 leading-relaxed mb-4">
                          Find ud af, hvordan du bliver en del af Vikar4, og hvad vores nuværende vikarer siger om os.
                        </p>

                        <div className="space-y-2.5">
                          <Link
                            to="/bliv-vikar"
                            className="group block bg-white border border-slate-200 hover:border-[#015D9A] rounded p-3 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="font-bold text-sm text-[#0f3d5c] group-hover:text-[#015D9A] transition-colors flex items-center gap-2">
                                <i className="fa-solid fa-circle-chevron-right text-xs text-[#015D9A]" />
                                <span>Bliv vikar</span>
                              </div>
                              <i className="fa-solid fa-arrow-right text-xs text-slate-400 group-hover:text-[#015D9A] group-hover:translate-x-0.5 transition-all" />
                            </div>
                            <p className="text-xs text-slate-500 mt-1 pl-5">
                              Læs om processen, vilkår, fleksibilitet og hvordan du opretter dig.
                            </p>
                          </Link>

                          <Link
                            to="/udtalelser-fra-vikarer"
                            className="group block bg-white border border-slate-200 hover:border-[#015D9A] rounded p-3 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="font-bold text-sm text-[#0f3d5c] group-hover:text-[#015D9A] transition-colors flex items-center gap-2">
                                <i className="fa-solid fa-circle-chevron-right text-xs text-[#015D9A]" />
                                <span>Udtalelser fra vikarer</span>
                              </div>
                              <i className="fa-solid fa-arrow-right text-xs text-slate-400 group-hover:text-[#015D9A] group-hover:translate-x-0.5 transition-all" />
                            </div>
                            <p className="text-xs text-slate-500 mt-1 pl-5">
                              Læs historier og personlige oplevelser fra vores vikarer.
                            </p>
                          </Link>
                        </div>
                      </div>

                      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                        <a
                          href="mailto:job@vikar4.dk"
                          className="text-xs font-bold text-[#015D9A] hover:underline inline-flex items-center gap-1.5"
                        >
                          <span>Send dit CV direkte til job@vikar4.dk</span>
                          <i className="fa-solid fa-envelope text-[10px]" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 8. Skal vi finde den løsning, der passer til jer? (Action Banner) */}
                <div className="border-t border-slate-200/80 pt-10 pb-4">
                  <div className="bg-[#0f3d5c] text-white rounded-md p-7 sm:p-8 space-y-4 shadow-sm relative overflow-hidden">
                    <div className="relative z-10">
                      <span className="text-xs uppercase font-bold tracking-wider text-blue-300">Tag dialogen i dag</span>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mt-1">
                        Skal vi finde den løsning, der passer til jer?
                      </h2>
                      <p className="text-slate-200 leading-relaxed text-sm sm:text-base mt-2 max-w-2xl">
                        Uanset om din virksomhed har brug for en vikar, eller du selv søger et fleksibelt arbejde, er du velkommen til at kontakte Vikar4. Ring eller skriv om jeres bemandingsbehov, eller send dit CV, hvis du søger job. Så tager vi en konkret dialog om mulighederne og det næste skridt.
                      </p>
                      <div className="flex flex-wrap items-center gap-4 pt-4">
                        <Link
                          to="/kontakt"
                          className="inline-flex items-center gap-2 rounded-md bg-[#015D9A] border border-blue-400/40 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#014d80] shadow-sm"
                        >
                          <span>Kontakt Vikar4 i dag</span>
                          <i className="fa-solid fa-arrow-right text-xs" />
                        </Link>
                        <a
                          href="tel:27606027"
                          className="inline-flex items-center gap-2 text-sm font-medium text-slate-200 hover:text-white transition-colors"
                        >
                          <i className="fa-solid fa-phone text-blue-300 text-xs" />
                          <span>Ring (+45) 27 60 60 27</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Right Column: Sticky Sidebar Callouts (4 of 12 columns) */}
              <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
                
                {/* Action Card 1: Job som vikar */}
                <div className="bg-slate-100 p-8 flex flex-col items-center justify-center text-center rounded-sm border border-slate-200/60 shadow-none">
                  <span className="text-xs uppercase font-bold tracking-wider text-[#015D9A] mb-2">
                    Søger du job?
                  </span>
                  <h3 className="text-xl font-bold text-[#0f3d5c] tracking-tight mb-2">
                    Ønsker du at blive vikar?
                  </h3>

                  {/* Users Icon Illustration */}
                  <div className="w-36 h-24 my-3 flex items-center justify-center text-[#015D9A]">
                    <svg className="w-full h-full" fill="none" viewBox="0 0 100 60" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M 15 45 C 15 35, 35 35, 35 45 Z" />
                      <circle cx="25" cy="28" r="7" />
                      <path d="M 65 45 C 65 35, 85 35, 85 45 Z" />
                      <circle cx="75" cy="28" r="7" />
                      <path className="stroke-3" fill="#f1f5f9" d="M 30 52 C 30 38, 70 38, 70 52 Z" />
                      <circle className="stroke-3" fill="#f1f5f9" cx="50" cy="28" r="9" />
                    </svg>
                  </div>

                  <p className="text-sm text-slate-600 leading-relaxed max-w-[260px] mb-4">
                    Send dit CV eller en kort introduktion til os, så kontakter vi dig hurtigst muligt.
                  </p>

                  <a
                    href="mailto:job@vikar4.dk"
                    className="w-full rounded-sm bg-[#015D9A] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#014d80] transition-colors"
                  >
                    job@vikar4.dk
                  </a>
                </div>

                {/* Action Card 2: Virksomheder / Bemandingsbehov */}
                <div className="bg-[#0f3d5c] p-8 flex flex-col items-center justify-center text-center rounded-sm text-white">
                  <span className="text-xs uppercase font-bold tracking-wider text-blue-200 mb-2">
                    For virksomheder
                  </span>
                  <h3 className="text-xl font-bold tracking-tight mb-2">
                    Mangler I medarbejdere?
                  </h3>
                  <p className="text-sm text-slate-200 leading-relaxed mb-6">
                    Få en uforpligtende snak om jeres behov i Nordjylland, Midtjylland eller resten af landet.
                  </p>

                  <div className="space-y-3 w-full">
                    <a
                      href="tel:+4525944444"
                      className="flex items-center justify-center gap-2 w-full rounded-sm bg-white text-[#0f3d5c] px-4 py-2.5 text-sm font-bold hover:bg-slate-100 transition-colors"
                    >
                      <i className="fa-solid fa-phone text-[#015D9A]" />
                      <span>Ring til os</span>
                    </a>
                    <Link
                      to="/kontakt"
                      className="flex items-center justify-center gap-2 w-full rounded-sm border border-white/30 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                    >
                      <span>Gå til kontaktformular</span>
                    </Link>
                  </div>
                </div>

              </aside>
            </div>
          </div>
        </section>

        {/* Quick Navigation Cards Section (Dark Mode Gradient as in Design Reference) */}
        <section className="relative w-full min-h-[500px] flex items-center justify-center bg-gradient-to-b from-[#10335c] to-[#03070c] py-24 px-6 sm:px-12 overflow-hidden">
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
              
              {/* Card 1: Bliv vikar */}
              <Link to="/bliv-vikar" className="flex flex-col items-center text-center group">
                {/* Icon Badge */}
                <div className="w-36 h-36 rounded-full bg-white/10 border border-white/5 flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-105 group-hover:bg-white/15 group-hover:border-white/10">
                  {/* Inline Handshake/Gears Icon */}
                  <svg className="w-16 h-16 text-white opacity-90" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.97 5.97 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                  </svg>
                </div>
                {/* Title */}
                <h3 className="text-2xl font-semibold text-white mb-3 tracking-wide group-hover:text-[#015D9A] transition-colors">Bliv vikar</h3>
                {/* Description */}
                <p className="text-base text-slate-300/90 font-light leading-relaxed max-w-[280px]">
                  Læs hvordan du bliver vikar lynhurtigt.
                </p>
              </Link>

              {/* Card 2: Virksomhed */}
              <Link to="/hvad-tilbyder-vi" className="flex flex-col items-center text-center group">
                {/* Icon Badge */}
                <div className="w-36 h-36 rounded-full bg-white/10 border border-white/5 flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-105 group-hover:bg-white/15 group-hover:border-white/10">
                  {/* Inline Building Icon */}
                  <svg className="w-16 h-16 text-white opacity-90" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75Z" />
                  </svg>
                </div>
                {/* Title */}
                <h3 className="text-2xl font-semibold text-white mb-3 tracking-wide group-hover:text-[#015D9A] transition-colors">Virksomhed</h3>
                {/* Description */}
                <p className="text-base text-slate-300/90 font-light leading-relaxed max-w-[280px]">
                  Læs om hvad vi tilbyder
                </p>
              </Link>

              {/* Card 3: Referencer */}
              <Link to="/referencer" className="flex flex-col items-center text-center group">
                {/* Icon Badge */}
                <div className="w-36 h-36 rounded-full bg-white/10 border border-white/5 flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-105 group-hover:bg-white/15 group-hover:border-white/10">
                  {/* Inline Profile Arrow Icon */}
                  <svg className="w-16 h-16 text-white opacity-90" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                  </svg>
                </div>
                {/* Title */}
                <h3 className="text-2xl font-semibold text-white mb-3 tracking-wide group-hover:text-[#015D9A] transition-colors">Referencer</h3>
                {/* Description */}
                <p className="text-base text-slate-300/90 font-light leading-relaxed max-w-[280px]">
                  Det er ikke kun os selv der mener vi er et attraktivt vikarbureau.
                </p>
              </Link>

              {/* Card 4: Kontakt os */}
              <Link to="/kontakt" className="flex flex-col items-center text-center group">
                {/* Icon Badge */}
                <div className="w-36 h-36 rounded-full bg-white/10 border border-white/5 flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-105 group-hover:bg-white/15 group-hover:border-white/10">
                  {/* Inline Contact Directory Icon */}
                  <svg className="w-16 h-16 text-white opacity-90" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 0 13.5 2.25H15c1.03 0 1.9.693 2.166 1.638m-7.377 12.408-.621 3.308a2.25 2.25 0 0 0 2.25 2.25h1.5a2.25 2.25 0 0 0 2.25-2.25l-.621-3.308M12 9a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z" />
                  </svg>
                </div>
                {/* Title */}
                <h3 className="text-2xl font-semibold text-white mb-3 tracking-wide group-hover:text-[#015D9A] transition-colors">Kontakt os</h3>
                {/* Description */}
                <p className="text-base text-slate-300/90 font-light leading-relaxed max-w-[280px]">
                  Du er altid velkommen til at kontakte os for et tilbud.
                </p>
              </Link>

            </div>
          </div>
        </section>

        {/* Call-to-Action / Info Section */}
        <section className="py-20 bg-slate-50 border-t border-slate-100">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="max-w-3xl flex flex-col gap-6">
              {/* Heading & Subtitle */}
              <div className="flex flex-col gap-3">
                <span className="text-sm font-semibold uppercase tracking-wider text-[#0f3d5c]">
                  Vi har den vikar DU står og mangler
                </span>
                <h2 className="text-3xl font-bold text-[#0f3d5c] leading-tight">
                  Har du spørgsmål eller er interesseret?
                </h2>
              </div>

              {/* Text & Action */}
              <div className="flex flex-col gap-4">
                <p className="text-slate-700 leading-relaxed">
                  Du er altid velkommen til at kontakte os for yderligere spørgsmål, eller hvis du er interesseret.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  Hos Vikar4 står vi altid klar til at give dig et uforpligtende tilbud!
                </p>
                <div className="pt-2">
                  <Link
                    to="/kontakt"
                    className="inline-flex self-start rounded-sm bg-[#015D9A] px-8 py-3 text-sm font-semibold text-white transition-all hover:bg-[#014d80] shadow-none"
                  >
                    Kontakt os
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    )
  }

  // Standard Page Layout
  return (
    <div className="bg-white">
      {/* Page Header - Hero Banner Component */}
      <section className="relative h-[240px] md:h-[320px] bg-[#015D9A] flex items-center justify-center overflow-hidden">
        {/* Background Image with Cool-toned Dark Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center mix-blend-multiply opacity-60 bg-[url('https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/7b6c8b8c-dd85-4faa-a1de-b9b48f1b2725/industrial-subpage-hero.jpg')]"
        />
        {/* Centered Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-wide">
            {getLocalizedValue(page.title)}
          </h1>
        </div>
      </section>

      {/* Main Content - Two-Column Main Layout Content */}
      <main className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left: Content Column */}
          <article className="lg:col-span-8">
            {isPrivatlivspolitik ? (
              <div className="space-y-8">
                {/* Intro text */}
                {intro && (
                  <div 
                    className="prose-custom [&_h1]:hidden mb-8"
                    onClick={handleIntroClick}
                    dangerouslySetInnerHTML={{ __html: intro }}
                  />
                )}
                
                {/* Accordion List */}
                <div className="space-y-4">
                  {accordionItems.map((item, index) => {
                    const isOpen = !!openAccordionIndices[index];
                    return (
                      <div 
                        key={index} 
                        id={item.id || `accordion-item-${index}`} 
                        className="border border-slate-200 rounded-sm overflow-hidden bg-white shadow-none scroll-mt-28"
                      >
                        <button
                          onClick={() => toggleAccordion(index)}
                          className="w-full flex items-center justify-between p-5 text-left bg-slate-50 hover:bg-slate-100 transition-colors focus:outline-none group cursor-pointer"
                        >
                          <span 
                            className="text-base sm:text-lg font-bold text-[#0f3d5c] group-hover:text-[#015D9A] transition-colors"
                            dangerouslySetInnerHTML={{ __html: item.title }}
                          />
                          <i className={`fa-solid fa-chevron-down text-slate-400 group-hover:text-[#015D9A] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isOpen && (
                          <div className="p-6 border-t border-slate-200 bg-white">
                            <div 
                              className="prose-custom"
                              dangerouslySetInnerHTML={{ __html: item.contentHtml }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div 
                className="prose-custom [&_h1]:hidden"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            )}
          </article>

          {/* Right: Sidebar Action Box */}
          <aside className="lg:col-span-4">
            <div className="bg-slate-100 p-8 text-center flex flex-col items-center justify-center border border-slate-200/50 rounded-sm shadow-none">
              <h3 className="text-lg font-bold text-[#0f3d5c] mb-6">
                Ønsker du at blive vikar?
              </h3>
              {/* Linear Brand Icon */}
              <div className="flex justify-center mb-6 text-[#015D9A] w-40 h-28">
                <svg className="w-full h-full" fill="none" viewBox="0 0 100 60" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {/* Left User Back */}
                  <path d="M 15 45 C 15 35, 35 35, 35 45 Z" />
                  <circle cx="25" cy="28" r="7" />
                  {/* Right User Back */}
                  <path d="M 65 45 C 65 35, 85 35, 85 45 Z" />
                  <circle cx="75" cy="28" r="7" />
                  {/* Middle Main User Front (Larger) */}
                  <path className="stroke-3" fill="#f1f5f9" d="M 30 52 C 30 38, 70 38, 70 52 Z" />
                  <circle className="stroke-3" fill="#f1f5f9" cx="50" cy="28" r="9" />
                </svg>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                Hvis du ønsker at blive vikar, så skriv en mail til
                <a href="mailto:job@vikar4.dk" className="block mt-1 font-bold text-[#015D9A] hover:text-[#014d80] hover:underline break-all">
                  job@vikar4.dk
                </a>
              </p>
            </div>
          </aside>

        </div>
      </main>
    </div>
  )
}
